import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { DocComparerGroup } from './doc-comparer-group';
import {
  getInconsistencySourceDisplayName,
  getInconsistencySourceTooltip,
  getInconsistencyTypeDisplayName,
  getInconsistencyTypeTooltip,
  InconsistencyLevel,
} from '../../../../../backend/src/matter/inconsistency/inconsistency-display';
import { Permission } from '../../../../../backend/src/shared/action-registry';
import { Relevance } from '../../../../../backend/src/types/types';
import { useHasPermission, PermissionGate } from '../../../hooks/useHasPermission';
import { ButtonGroup } from '../shadcn/button-group';
import { useUpdateInconsistency } from '@/api/mutations/useUpdateInconsistency';
import { Inconsistency } from '@/api/types/backend-dto-types';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { Chip } from '@/new/components/chip';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Dialog, DialogContent, DialogTrigger } from '@/new/components/shadcn/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { TextWithReferences } from '@/new/components/text-references';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';
import { useStore } from '@/store';

export function InconsistencyCard({
  inconsistency,
  inInconsistencyDrawer = false,
  renderAsAccordion = true,
  onDelete,
  isInconsistencySelected,
  toggleInconsistencySelection,
}: {
  inconsistency: Inconsistency;
  inInconsistencyDrawer?: boolean;
  renderAsAccordion?: boolean;
  onDelete?: () => void;
  isInconsistencySelected?: (inconsistencyId: string) => boolean;
  toggleInconsistencySelection?: (inconsistencyId: string, checked: boolean | 'indeterminate') => void;
}) {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const { openReferenceDrawer } = useStore();
  const { hasPermission } = useHasPermission();
  const canEditInconsistency = hasPermission(Permission.InconsistencyEdit);
  const { mutate: updateInconsistency } = useUpdateInconsistency({ matterId, subsetId });
  const inconsistencyLink = `${window.location.origin}/matters/${matterId}/subsets/${subsetId}/inconsistencies/${inconsistency.id}`;

  const hasRelevantFacts = useMemo(
    () =>
      !inInconsistencyDrawer &&
      inconsistency.facts.some((f) => f.fact.relevances?.some((r) => r.relevance === Relevance.Relevant) === true),
    [inconsistency.facts, inInconsistencyDrawer],
  );

  // group facts by 'name' which is the group name, e.g. 'Different evidence...'
  const groupedFacts = useMemo(() => Object.groupBy(inconsistency.facts, ({ name }) => name), [inconsistency.facts]);

  const headerContent = (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div className="flex items-center gap-2">
        {isInconsistencySelected && toggleInconsistencySelection && (
          <Checkbox
            className="mr-1.5"
            checked={isInconsistencySelected(inconsistency.id)}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(checked) => toggleInconsistencySelection(inconsistency.id, checked)}
          />
        )}

        {canEditInconsistency ? (
          <Select
            value={inconsistency.level}
            onValueChange={(level) =>
              updateInconsistency({ inconsistencyId: inconsistency.id, level: level as InconsistencyLevel })
            }
          >
            <SelectTrigger
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'inline-flex h-auto w-auto items-center gap-1.5 rounded-md border px-2 py-0.75 text-xs font-medium whitespace-nowrap [&>svg]:ml-0 [&>svg]:size-3.5',
                CHIP_COLOURS.levels[inconsistency.level],
              )}
            >
              <Icon className="lucide--shield-alert" />
              <SelectValue>{capitalizeFirstLetter(inconsistency.level)} Inconsistency</SelectValue>
            </SelectTrigger>

            <SelectContent onClick={(e) => e.stopPropagation()} onCloseAutoFocus={(e) => e.preventDefault()}>
              {Object.values(InconsistencyLevel).map((level) => (
                <SelectItem key={level} value={level}>
                  {capitalizeFirstLetter(level)} Inconsistency
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Chip className={`${CHIP_COLOURS.levels[inconsistency.level]} border`}>
            <Icon className={`lucide--shield-alert`} />
            <span>{capitalizeFirstLetter(inconsistency.level)} Inconsistency</span>
          </Chip>
        )}

        <Dialog>
          <DialogTrigger asChild>
            <Button size="sm" onClick={(e) => e.stopPropagation()} className="py-0.75">
              <Icon className="lucide--flip-horizontal-2" />
              <span>Compare Evidence</span>
            </Button>
          </DialogTrigger>

          <DialogContent
            className="flex h-9/10 max-h-none w-9/10 max-w-none gap-4 pb-3"
            onClick={(e) => e.stopPropagation()}
          >
            {Object.entries(groupedFacts).map(([group, facts]) =>
              facts ? <DocComparerGroup key={group} title={group} facts={facts} /> : null,
            )}
          </DialogContent>
        </Dialog>
      </div>

      {!inInconsistencyDrawer && (
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {hasRelevantFacts && (
            <Chip className="text-green-600">
              <Icon className="lucide--circle-check" />
              <span>Contains Relevant Facts</span>
            </Chip>
          )}

          <Popover>
            <PopoverTrigger onClick={(e) => e.stopPropagation()}>
              <Chip className={`${CHIP_COLOURS.inconsistencySource} border`}>
                <span>{getInconsistencySourceDisplayName(inconsistency.source)}</span>
              </Chip>
            </PopoverTrigger>
            <PopoverContent className="mx-4">{getInconsistencySourceTooltip(inconsistency.source)}</PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger onClick={(e) => e.stopPropagation()}>
              <Chip className={`${CHIP_COLOURS.inconsistencyType} border`}>
                <span>{getInconsistencyTypeDisplayName(inconsistency.type)}</span>
              </Chip>
            </PopoverTrigger>
            <PopoverContent className="mx-4">{getInconsistencyTypeTooltip(inconsistency.type)}</PopoverContent>
          </Popover>

          <ButtonGroup>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(inconsistencyLink);
                toast.success('Link to inconsistency copied to clipboard');
              }}
            >
              <Icon className="lucide--link" /> Share
            </Button>

            {onDelete && (
              <PermissionGate permission={Permission.InconsistencyDelete} uiMode="disabled">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDeleteOpen(true);
                  }}
                  className="-ml-0.5"
                >
                  <Icon className="lucide--trash mb-px" /> Delete
                </Button>
              </PermissionGate>
            )}
          </ButtonGroup>
        </div>
      )}
    </div>
  );

  const detailsContent = (
    <>
      {Object.entries(groupedFacts).map(([group, facts], i) => (
        <div key={group}>
          <div className="relative py-1 pb-0.5 pl-6">
            <div className={`absolute left-0 h-full w-0.75 rounded-full bg-gray-200`} />

            <p className="my-2 font-medium">{group}</p>

            <div className="text-xs">
              {facts?.map((fact) => (
                <div key={fact.id}>
                  <div className="flex items-center gap-2">
                    {fact.fact.locations.map((location) => (
                      <div className="flex items-center gap-1.5" key={location.id}>
                        <p
                          // <p> with an onClick is not ideal but button can't be a descendant of button
                          className={cn(
                            'cursor-pointer text-blue-600 duration-150 hover:text-blue-700',
                            inInconsistencyDrawer && 'cursor-default',
                          )}
                          onClick={() =>
                            !inInconsistencyDrawer && openReferenceDrawer({ factId: location.factId, caseId: subsetId })
                          }
                        >
                          {location.doc.file.name}
                        </p>
                        {location.doc.type && <Chip className={`${CHIP_COLOURS.type}`}>{location.doc.type}</Chip>}
                        {location.doc.isPleading === true && (
                          <Chip className={`${CHIP_COLOURS.isPleading} border`}>Pleading</Chip>
                        )}
                        {location.doc.dateText && (
                          <Chip className={`${CHIP_COLOURS.default}`}>{location.doc.dateText}</Chip>
                        )}
                      </div>
                    ))}
                  </div>

                  <p className="mt-1 mb-3 text-gray-600">{fact.fact.description}</p>
                </div>
              ))}
            </div>
          </div>

          {i < Object.keys(groupedFacts).length - 1 && <Icon className="lucide--arrow-up-down my-4 pl-16" />}
        </div>
      ))}
    </>
  );

  return (
    <>
      {renderAsAccordion ? (
        <AccordionItem value={inconsistency.id} className="w-full px-4 pb-2 not-last:border-b">
          <AccordionTrigger className="w-full pt-1">
            <div className="mr-3 w-full font-normal text-gray-800">
              {headerContent}
              <TextWithReferences content={inconsistency.description} disableReferenceClick={inInconsistencyDrawer} />
            </div>
          </AccordionTrigger>

          <AccordionContent className="mt-2 mb-6">{detailsContent}</AccordionContent>
        </AccordionItem>
      ) : (
        <div className="w-full px-4 pb-2">
          <div className="w-full pt-1 font-normal text-gray-800">
            {headerContent}
            <TextWithReferences content={inconsistency.description} disableReferenceClick={inInconsistencyDrawer} />
          </div>

          <div className="mt-6 mb-6">{detailsContent}</div>
        </div>
      )}

      {onDelete && (
        <ConfirmationModal
          isOpen={confirmDeleteOpen}
          onOpenChange={setConfirmDeleteOpen}
          title="Delete Inconsistency"
          onConfirm={() => {
            onDelete();
            setConfirmDeleteOpen(false);
          }}
          isLoading={false}
        >
          <p>Are you sure you want to delete this inconsistency? This action cannot be undone.</p>
        </ConfirmationModal>
      )}
    </>
  );
}
